// ============================================================
// Reusable MultiSelect — wraps Radix Select with a deferred-add
// pattern so picking an option that gets immediately filtered
// out of <SelectContent> doesn't crash with:
//   NotFoundError: Failed to execute 'removeChild' on 'Node'
//
// Use this anywhere you need a "pick many from a list" UX where
// chosen items are removed from the dropdown after selection.
// ============================================================

import * as React from "react";
import { X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Optional secondary line shown under the label inside the dropdown. */
  description?: string;
  disabled?: boolean;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Hide already-selected items from the dropdown (default: true). */
  hideSelected?: boolean;
  /** Render selected items as removable badges below the trigger (default: true). */
  showBadges?: boolean;
  /** Disable the entire control. */
  disabled?: boolean;
  /** Empty-state message shown inside the dropdown when no options remain. */
  emptyMessage?: string;
  className?: string;
  triggerClassName?: string;
  id?: string;
  "aria-label"?: string;
}

/**
 * MultiSelect — single-pick Radix Select reused as a multi-pick.
 *
 * Why the setTimeout: when the user picks an item we (a) close the dropdown,
 * (b) add the value to state, and (c) re-render <SelectContent> with that
 * item filtered out. If steps (b)/(c) run synchronously, React unmounts the
 * <SelectItem> while Radix is still mid-close-animation on that exact node,
 * which throws "Failed to execute 'removeChild' on 'Node'". Deferring the
 * state update with a 0ms timeout lets the animation finish first.
 */
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "Select…",
  hideSelected = true,
  showBadges = true,
  disabled = false,
  emptyMessage = "No options available",
  className,
  triggerClassName,
  id,
  ...rest
}: MultiSelectProps) {
  const valueSet = React.useMemo(() => new Set(value), [value]);

  const visibleOptions = React.useMemo(
    () => (hideSelected ? options.filter((o) => !valueSet.has(o.value)) : options),
    [options, valueSet, hideSelected]
  );

  const handleSelect = React.useCallback(
    (selected: string) => {
      if (!selected) return;
      // Defer so Radix can finish the close animation on the <SelectItem>
      // before we re-render and unmount it. Without this you get a
      // NotFoundError: Failed to execute 'removeChild' on 'Node'.
      setTimeout(() => {
        if (valueSet.has(selected)) return;
        onChange([...value, selected]);
      }, 0);
    },
    [onChange, value, valueSet]
  );

  const handleRemove = React.useCallback(
    (target: string) => {
      onChange(value.filter((v) => v !== target));
    },
    [onChange, value]
  );

  const labelFor = React.useCallback(
    (v: string) => options.find((o) => o.value === v)?.label ?? v,
    [options]
  );

  return (
    <div className={cn("space-y-2", className)}>
      <Select
        // value is intentionally undefined — this Select is acting as a picker,
        // not a controlled single-value field. Empty string is invalid in Radix.
        value={undefined}
        onValueChange={handleSelect}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          aria-label={rest["aria-label"]}
          className={triggerClassName}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {visibleOptions.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          ) : (
            visibleOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                <div className="flex flex-col">
                  <span>{opt.label}</span>
                  {opt.description && (
                    <span className="text-xs text-muted-foreground">
                      {opt.description}
                    </span>
                  )}
                </div>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {showBadges && value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((v) => (
            <Badge key={v} variant="secondary" className="flex items-center gap-1">
              {labelFor(v)}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${labelFor(v)}`}
                  onClick={() => handleRemove(v)}
                  className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
